import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Image, Plus, Upload, X } from "lucide-react";
import { api, apiError, imgUrl } from "@/lib/api";

const FIELDS = [
  ["hero_subtitle", "Hero Subtitle", "text"],
  ["hero_image", "عکس صفحه‌ی اول (Hero Image)", "image"],
  ["about_text", "About Text", "textarea"],
  ["about_image", "عکس بخش درباره‌ی من (About Image)", "image"],
  ["email_logo", "لوگوی بالای ایمیل‌ها (Email Logo)", "image"],
  ["craft_image", "عکس بخش سه‌گانه‌ی صفحه‌ی اول (Craft Image)", "image"],
  ["prep_guide", "Prep Guide — به ایمیل تأیید سشن اضافه می‌شه (هر خط یک پاراگراف)", "textarea"],
  ["deposit_amount", "Session Deposit $ (دکمه‌ی پرداخت آنلاین توی پورتال مشتری)", "text"],
  ["zelle_email", "Zelle Email (برای یادآوری پرداخت)", "text"],
  ["zelle_recipient", "Zelle Recipient Name (نام گیرنده‌ی Zelle)", "text"],
  ["notification_email", "Admin Notification Email", "text"],
  ["contact_phone", "Contact Phone (shows Call/Text buttons on booking page)", "text"],
  ["contact_email", "Public Contact Email (shown in Contact section)", "text"],
  ["app_store_url", "App Store Link (shows download button in About)", "text"],
  ["google_play_url", "Google Play Link (shows download button in About)", "text"],
];

const BOOKING_FIELDS = [
  ["preferred_date", "Preferred Date"],
  ["alternative_date", "Alternative Date"],
  ["preferred_time", "Preferred Time"],
  ["num_people", "Number of People"],
  ["location", "Location / Area"],
  ["instagram", "Instagram Handle"],
  ["message", "Tell me about your session"],
  ["referral", "How did you hear about me?"],
];

export default function SettingsTab() {
  const [form, setForm] = useState({});
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerFor, setPickerFor] = useState(null);
  const [uploadFor, setUploadFor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      const s = r.data || {};
      if (!Array.isArray(s.social_links)) {
        s.social_links = [s.instagram_url, s.instagram_url_2].filter(Boolean).map((url) => ({ label: "Instagram", url }));
      }
      setForm(s);
    }).catch((e) => toast.error(apiError(e)));
    api.get("/admin/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, social_links: (form.social_links || []).filter((l) => l.url && l.url.trim()) };
      const r = await api.put("/admin/settings", payload);
      setForm(r.data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file || !uploadFor) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, [uploadFor]: up.data.url }));
      toast.success("Image uploaded — press Save Settings");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setUploading(false);
      fileRef.current.value = "";
    }
  };

  const setLink = (i, patch) =>
    setForm((prev) => ({ ...prev, social_links: (prev.social_links || []).map((x, j) => (j === i ? { ...x, ...patch } : x)) }));

  const inputCls = "w-full border border-[#E5E5E2] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]";

  return (
    <form onSubmit={save} data-testid="admin-settings-form" className="bg-white border border-[#E5E5E2] p-8 max-w-2xl space-y-6">
      <input ref={fileRef} type="file" accept="image/*" hidden data-testid="settings-image-upload"
        onChange={(e) => uploadImage(e.target.files[0])} />
      {FIELDS.map(([key, label, type]) => (
        <div key={key}>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">{label}</label>
          {type === "textarea" ? (
            <textarea rows={4} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              data-testid={`settings-${key}`} className={`${inputCls} resize-none`} />
          ) : (
            <input value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
              data-testid={`settings-${key}`} className={inputCls} />
          )}
          {type === "image" && (
            <div className="mt-2">
              <div className="flex items-center gap-2">
                {form[key] && <img src={imgUrl(form[key])} alt="" className="w-16 h-16 object-cover border border-[#E5E5E2]" />}
                <button type="button" onClick={() => { setUploadFor(key); fileRef.current?.click(); }}
                  data-testid={`settings-upload-${key}`} disabled={uploading}
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] bg-[#1A1A1A] text-white px-4 py-2.5 hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
                  <Upload size={12} /> {uploading && uploadFor === key ? "Uploading…" : "Upload Photo"}
                </button>
                <button type="button" onClick={() => setPickerFor(pickerFor === key ? null : key)}
                  data-testid={`settings-pick-${key}`}
                  className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] border border-[#E5E5E2] px-4 py-2.5 hover:border-[#1A1A1A] transition-colors">
                  <Image size={12} /> From Portfolio
                </button>
              </div>
              {pickerFor === key && (
                <div className="mt-3 border border-[#E5E5E2] p-3" data-testid={`settings-picker-${key}`}>
                  <div className="flex justify-between items-center mb-2">
                    <p className="text-[10px] uppercase tracking-[0.15em] text-[#7A7A7A]">Choose from your photos</p>
                    <button type="button" onClick={() => setPickerFor(null)}><X size={13} /></button>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {photos.map((p) => (
                      <button type="button" key={p.id}
                        onClick={() => { setForm({ ...form, [key]: p.url }); setPickerFor(null); toast.success("Selected — press Save Settings"); }}
                        className={`aspect-square overflow-hidden border-2 ${form[key] === p.url ? "border-[#1A1A1A]" : "border-transparent hover:border-[#7A7A7A]"}`}>
                        <img src={imgUrl(p.url)} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover" />
                      </button>
                    ))}
                    {photos.length === 0 && <p className="text-xs text-[#7A7A7A] col-span-full">No photos yet — upload one with the button above.</p>}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <div className="border-t border-[#E5E5E2] pt-6" data-testid="settings-social-section">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-1">Social & Website Links</p>
        <p className="text-xs text-[#B5B5B0] mb-4">هر لینکی می‌خوای اضافه یا کم کن — Instagram، TikTok، YouTube، وب‌سایت… آیکن هر کدوم خودکار درست انتخاب می‌شه و روی سایت نمایش داده می‌شن.</p>
        <div className="space-y-2">
          {(form.social_links || []).map((l, i) => (
            <div key={i} className="flex gap-2" data-testid={`settings-social-row-${i}`}>
              <input value={l.label || ""} placeholder="Label (e.g. TikTok)"
                onChange={(e) => setLink(i, { label: e.target.value })}
                data-testid={`settings-social-label-${i}`} className={`${inputCls} !w-44 shrink-0`} />
              <input value={l.url || ""} placeholder="https://…" dir="ltr"
                onChange={(e) => setLink(i, { url: e.target.value })}
                data-testid={`settings-social-url-${i}`} className={inputCls} />
              <button type="button" onClick={() => setForm((prev) => ({ ...prev, social_links: prev.social_links.filter((_, j) => j !== i) }))}
                data-testid={`settings-social-remove-${i}`} aria-label="Remove link"
                className="px-3 border border-[#E5E5E2] hover:border-[#1A1A1A] transition-colors shrink-0"><X size={14} /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setForm((prev) => ({ ...prev, social_links: [...(prev.social_links || []), { label: "", url: "" }] }))}
          data-testid="settings-social-add"
          className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] border border-[#E5E5E2] px-4 py-2.5 hover:border-[#1A1A1A] transition-colors">
          <Plus size={12} /> Add Link
        </button>
      </div>

      <div className="border-t border-[#E5E5E2] pt-6" data-testid="settings-banner-section">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-1">Seasonal Banner</p>
        <p className="text-xs text-[#B5B5B0] mb-4">بنر شیک بالای صفحه‌ی اصلی — مثلاً «Valentine Minis now booking» یا «Holiday Gift Cards». هر وقت خواستی روشن/خاموشش کن.</p>
        <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 mb-3 hover:border-[#1A1A1A] transition-colors" data-testid="settings-banner-enabled">
          <input type="checkbox" checked={!!form.banner_enabled}
            onChange={(e) => setForm({ ...form, banner_enabled: e.target.checked })}
            className="accent-[#1A1A1A] w-4 h-4" />
          Show banner on home page
        </label>
        <div className="space-y-3">
          <input value={form.banner_text || ""} onChange={(e) => setForm({ ...form, banner_text: e.target.value })}
            placeholder="Banner text — e.g. Holiday Gift Cards are here, give the gift of memories"
            data-testid="settings-banner-text" className={inputCls} />
          <div className="grid sm:grid-cols-2 gap-3">
            <input value={form.banner_cta || ""} onChange={(e) => setForm({ ...form, banner_cta: e.target.value })}
              placeholder="Button label (optional) — e.g. Shop Gift Cards"
              data-testid="settings-banner-cta" className={inputCls} />
            <input value={form.banner_link || ""} onChange={(e) => setForm({ ...form, banner_link: e.target.value })}
              placeholder="Link (optional) — e.g. /gift-cards" dir="ltr"
              data-testid="settings-banner-link" className={inputCls} />
          </div>
        </div>
      </div>

      <div className="border-t border-[#E5E5E2] pt-6" data-testid="settings-workshops-coming-soon-section">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-1">Workshops Page</p>
        <p className="text-xs text-[#B5B5B0] mb-4">وقتی روشن باشه، صفحه‌ی ورکشاپ‌ها پیام «Workshops Coming Soon» رو نشون می‌ده و لیست ورکشاپ‌ها و دکمه‌های رزرو مخفی می‌شن. ورکشاپ‌ها رو همچنان می‌تونی اینجا توی ادمین بسازی و ویرایش کنی.</p>
        <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 hover:border-[#1A1A1A] transition-colors" data-testid="settings-workshops-coming-soon-toggle">
          <input type="checkbox" checked={!!form.workshops_coming_soon}
            onChange={(e) => setForm({ ...form, workshops_coming_soon: e.target.checked })}
            className="accent-[#1A1A1A] w-4 h-4" />
          Show "Workshops Coming Soon" on the public Workshops page
        </label>
      </div>

      <div className="border-t border-[#E5E5E2] pt-6" data-testid="settings-review-section">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-1">Review Requests</p>
        <p className="text-xs text-[#B5B5B0] mb-4">چند روز بعد از هر سشن، خودکار به مشتری ایمیل درخواست نظر می‌ره. لینک صفحه‌ی نظرات Google/Yelp خودت رو اینجا بذار.</p>
        <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 mb-3 hover:border-[#1A1A1A] transition-colors" data-testid="settings-review-enabled">
          <input type="checkbox" checked={!!form.review_requests_enabled}
            onChange={(e) => setForm({ ...form, review_requests_enabled: e.target.checked })}
            className="accent-[#1A1A1A] w-4 h-4" />
          Automatically ask clients for a review after their session
        </label>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">Review page link (Google / Yelp)</label>
            <input value={form.review_url || ""} onChange={(e) => setForm({ ...form, review_url: e.target.value })}
              placeholder="https://g.page/r/…" dir="ltr" data-testid="settings-review-url" className={inputCls} />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">Days after session</label>
            <input type="number" min="1" max="30" value={form.review_days_after ?? 2}
              onChange={(e) => setForm({ ...form, review_days_after: Number(e.target.value) })}
              data-testid="settings-review-days" className={inputCls} />
          </div>
        </div>
      </div>

      <div className="border-t border-[#E5E5E2] pt-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-1">Calendar Emails & Reminders</p>
        <p className="text-xs text-[#B5B5B0] mb-4">Reminder emails before sessions and update emails when a session changes. (Confirmation of a new session is sent from the calendar form itself.)</p>
        <label className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 mb-3 hover:border-[#1A1A1A] transition-colors" data-testid="settings-calendar-emails">
          <input type="checkbox" checked={!!form.calendar_emails_enabled}
            onChange={(e) => setForm({ ...form, calendar_emails_enabled: e.target.checked })}
            className="accent-[#1A1A1A] w-4 h-4" />
          Send session update & reminder emails to clients
        </label>
        <div>
          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-2">Reminder days before session</label>
          <input type="number" min="0" max="14" value={form.reminder_days_before ?? 1}
            onChange={(e) => setForm({ ...form, reminder_days_before: Number(e.target.value) })}
            data-testid="settings-reminder-days" className="w-32 border border-[#E5E5E2] px-4 py-3 text-sm outline-none focus:border-[#1A1A1A]" />
        </div>
      </div>
      <div className="border-t border-[#E5E5E2] pt-6">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#7A7A7A] mb-1">Booking Form Fields</p>
        <p className="text-xs text-[#B5B5B0] mb-4">Uncheck any field to hide it from the booking form.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {BOOKING_FIELDS.map(([key, label]) => {
            const visible = !(form.booking_hidden_fields || []).includes(key);
            return (
              <label key={key} data-testid={`settings-field-toggle-${key}`}
                className="flex items-center gap-3 text-sm text-[#4A4A4A] cursor-pointer border border-[#E5E5E2] px-4 py-3 hover:border-[#1A1A1A] transition-colors">
                <input type="checkbox" checked={visible}
                  onChange={() => {
                    const cur = form.booking_hidden_fields || [];
                    setForm({ ...form, booking_hidden_fields: visible ? [...cur, key] : cur.filter((k) => k !== key) });
                  }}
                  className="accent-[#1A1A1A] w-4 h-4" />
                {label}
              </label>
            );
          })}
        </div>
      </div>
      <button type="submit" disabled={saving} data-testid="settings-save"
        className="bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] px-8 py-3 hover:bg-[#4A4A4A] transition-colors disabled:opacity-50">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </form>
  );
}
